import { supabase } from '@/services/supabase';
import { logger, LogCategory } from '@/services/logger';
import { DOCUMENT_STATUS } from '@/types/document';
import {
  Document,
  DocumentCreate,
  DocumentUpdate,
  DocumentQueryOptions
} from '../models/types';

/**
 * Service for document management operations
 */
export class DocumentService {
  /**
   * Create a document
   * @param document Document data
   * @returns The created document
   */
  async createDocument(document: DocumentCreate): Promise<Document> {
    try {
      logger.info('Creating document', LogCategory.DATABASE, { document });
      
      const { data, error } = await supabase
        .from('documents')
        .insert({
          ...document,
          created_at: new Date().toISOString()
        })
        .select()
        .single();
      
      if (error) {
        throw error;
      }

      logger.info('Document created successfully', LogCategory.DATABASE, { documentId: data.id });
      
      return data;
    } catch (error) {
      logger.error('Error creating document', LogCategory.DATABASE, { document, error });
      throw error;
    }
  }

  /**
   * Get a document by ID
   * @param documentId The document ID
   * @returns The document or null if not found
   */
  async getDocumentById(documentId: string): Promise<Document | null> {
    try {
      const { data, error } = await supabase
        .from('documents')
        .select('*')
        .eq('uuid', documentId)
        .single();
      
      if (error) {
        if (error.code === 'PGRST116') {
          // No rows returned
          return null;
        }
        throw error;
      }
      
      return data;
    } catch (error) {
      logger.error('Error fetching document by ID', LogCategory.DATABASE, {
        documentId,
        error
      });
      
      if (error instanceof Error && error.message.includes('PGRST116')) {
        return null;
      }
      
      throw error;
    }
  }

  /**
   * List documents with filtering
   * @param options Query options
   * @returns Array of documents
   */
  async listDocuments(options?: DocumentQueryOptions): Promise<Document[]> {
    try {
      let query = supabase
        .from('documents')
        .select('*');
      
      // Apply filters
      if (options?.status) {
        query = query.eq('status', options.status);
      }
      
      if (options?.report_type) {
        query = query.eq('type', options.report_type);
      }
      
      if (options?.createdBy) {
        query = query.eq('created_by', options.createdBy);
      }
      
      if (options?.forecastId) {
        query = query.eq('forecast_id', options.forecastId);
      }
      
      if (options?.startDate) {
        query = query.gte('created_at', options.startDate);
      }
      
      if (options?.endDate) {
        query = query.lte('created_at', options.endDate);
      }
      
      // Apply sorting
      if (options?.sortBy) {
        const order = options.sortOrder || 'desc';
        query = query.order(options.sortBy, { ascending: order === 'asc' });
      } else {
        // Default sort by created_at desc
        query = query.order('created_at', { ascending: false });
      }
      
      // Apply pagination
      if (options?.limit) {
        query = query.limit(options.limit);
      }
      
      if (options?.offset) {
        query = query.range(options.offset, options.offset + (options.limit || 10) - 1);
      }
      
      const { data, error } = await query;
      
      if (error) {
        throw error;
      }
      
      return data || [];
    } catch (error) {
      logger.error('Error listing documents', LogCategory.DATABASE, {
        options,
        error
      });
      throw error;
    }
  }

  /**
   * Update a document
   * @param documentId The document ID
   * @param updates The updates to apply
   * @returns The updated document
   */
  async updateDocument(documentId: string, updates: DocumentUpdate): Promise<Document> {
    try {
      const { data, error } = await supabase
        .from('documents')
        .update({
          ...updates,
          updated_at: new Date().toISOString()
        })
        .eq('uuid', documentId)
        .select()
        .single();
      
      if (error) {
        throw error;
      }
      
      if (!data) {
        throw new Error(`Document with ID ${documentId} not found`);
      }
      
      return data;
    } catch (error) {
      logger.error('Error updating document', LogCategory.DATABASE, {
        documentId,
        updates,
        error
      });
      throw error;
    }
  }

  /**
   * Delete a document
   * @param documentId The document ID
   * @returns True if successful
   */
  async deleteDocument(documentId: string): Promise<boolean> {
    try {

      // TODO: Check whether a file is attached to the document before deleting
      // in the future, we want to delete the file record in the database and the file itself from storage
      const { error } = await supabase
        .from('documents')
        .delete()
        .eq('uuid', documentId);
      
      if (error) {
        throw error;
      }
      
      return true;
    } catch (error) {
      logger.error('Error deleting document', LogCategory.DATABASE, {
        documentId,
        error
      });
      throw error;
    }
  }

  /**
   * Get documents by forecast ID
   * @param forecastId The forecast ID
   * @returns Array of documents
   */
  async getDocumentsByForecastId(forecastId: string): Promise<Document[]> {
    try {
      const { data, error } = await supabase
        .from('documents')
        .select('*')
        .eq('forecast_id', forecastId)
        .order('created_at', { ascending: false });
      
      if (error) {
        throw error;
      }
      
      return data || [];
    } catch (error) {
      logger.error('Error fetching documents by forecast ID', LogCategory.DATABASE, {
        forecastId,
        error
      });
      throw error;
    }
  }

  /**
   * Update document status
   * @param documentId The document ID
   * @param status The new status
   * @param updatedBy The user ID who updated the document
   * @returns The updated document
   */
  async updateDocumentStatus(
    documentId: string,
    status: DOCUMENT_STATUS,
    updatedBy: string
  ): Promise<Document> {
    return this.updateDocument(documentId, {
      status,
      updated_by: updatedBy
    });
  }
}
